/**
 * Barcode / SKU / name quick-add against cached catalog — plan/09, plan/28.
 */

export type CatalogLookupItem = {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
};

/** Trim + collapse internal whitespace; scanners often send trailing CR. */
export function normalizeScan(raw: string): string {
  return raw.replace(/[\r\n]+/g, "").trim().replace(/\s+/g, " ");
}

/**
 * Prefer exact barcode, then exact SKU (case-insensitive), then unique name
 * prefix / contains. Ambiguous name matches → null (caller shows message).
 */
export function findCatalogByScan<T extends CatalogLookupItem>(
  items: readonly T[],
  raw: string,
): { match: T } | { ambiguous: true; count: number } | null {
  const q = normalizeScan(raw);
  if (!q) return null;

  const byBarcode = items.find(
    (i) => i.barcode != null && i.barcode === q,
  );
  if (byBarcode) return { match: byBarcode };

  const qLower = q.toLowerCase();
  const bySku = items.find(
    (i) => i.sku != null && i.sku.toLowerCase() === qLower,
  );
  if (bySku) return { match: bySku };

  const byName = items.filter((i) => {
    const n = i.name.toLowerCase();
    return n === qLower || n.startsWith(qLower) || n.includes(qLower);
  });
  if (byName.length === 1) return { match: byName[0]! };
  if (byName.length > 1) return { ambiguous: true, count: byName.length };
  return null;
}
